/**
 * Video Section Component - Lazy Loaded
 * Handles video sections with optimized loading and singleton pattern
 */

// Track initialized sections to prevent duplicates
const initializedSections = new WeakSet();

// Global registry to manage all video instances
const videoInstances = new Set();

class VideoSection {
  constructor(element) {
    // Prevent double initialization
    if (initializedSections.has(element)) {
      return;
    }
    initializedSections.add(element);
    
    this.section = element;
    this.sectionId = element.dataset.sectionId;
    this.videoType = this.getVideoType();
    this.settings = this.getSettings();
    
    // DOM elements
    this.videoElement = null;
    this.posterElement = null;
    this.playButton = null;
    this.youtubeThumb = null;
    this.vimeoThumb = null;
    
    // State
    this.isVideoLoaded = false;
    this.isPlaying = false;
    this.observer = null;
    this.playTimeout = null;
    this.pauseTimeout = null;
    
    // Lottie aspect ratios
    this.desktopAspectRatio = null;
    this.mobileAspectRatio = null;
    
    this.init();
    
    // Register this instance globally
    videoInstances.add(this);
  }

  init() {
    console.log('VideoSection: Initializing section', {
      sectionId: this.sectionId,
      videoType: this.videoType,
      settings: this.settings
    });
    
    this.cacheElements();
    this.setupIntersectionObserver();
    this.bindEvents();
    this.initVideoType();
    this.updatePlayButtonState();
  }

  cacheElements() {
    // Support both video-section and video-with-content sections
    this.videoElement = this.section.querySelector('.video-section__video') || 
                       this.section.querySelector('.video-with-content__video-element');
    
    this.posterElement = this.section.querySelector('.video-section__poster') || 
                        this.section.querySelector('.video-with-content__poster');
    
    this.playButton = this.section.querySelector('.video-section__play-button') || 
                     this.section.querySelector('.video-with-content__play-button');
    
    this.youtubeThumb = this.section.querySelector('.video-section__youtube-thumb') || 
                        this.section.querySelector('.video-with-content__youtube-thumb');
    
    this.vimeoThumb = this.section.querySelector('.video-section__vimeo-thumb') || 
                      this.section.querySelector('.video-with-content__vimeo-thumb');
    
    this.lottieContainer = this.section.querySelector('.video-section__lottie-container');
    this.lottieDesktopContainer = this.section.querySelector('.video-section__lottie-desktop');
    this.lottieMobileContainer = this.section.querySelector('.video-section__lottie-mobile');
    
    // Debug logging
    console.log('VideoSection: Cached elements', {
      sectionId: this.sectionId,
      videoType: this.getVideoType(),
      hasLottieContainer: !!this.lottieContainer,
      hasLottieDesktop: !!this.lottieDesktopContainer,
      hasLottieMobile: !!this.lottieMobileContainer,
      lottieUrl: this.lottieContainer?.dataset.lottieUrl
    });
  }

  getVideoType() {
    // Support both video-section and video-with-content sections
    if (this.section.querySelector('.video-section__video') || 
        this.section.querySelector('.video-with-content__video-element')) return 'upload';
    if (this.section.querySelector('.video-section__youtube-thumb') || 
        this.section.querySelector('.video-with-content__youtube-thumb')) return 'youtube';
    if (this.section.querySelector('.video-section__vimeo-thumb') || 
        this.section.querySelector('.video-with-content__vimeo-thumb')) return 'vimeo';
    if (this.section.querySelector('.video-section__lottie-container')) return 'lottie';
    return null;
  }

  getSettings() {
    return {
      autoplayOnView: this.section.dataset.autoplayOnView !== 'false',
      muted: this.section.dataset.muted !== 'false',
      loop: this.section.dataset.loop === 'true',
      controls: this.section.dataset.controls !== 'false'
    };
  }

  setupIntersectionObserver() {
    // Always set up observer for uploaded videos to handle pause-on-out-of-view
    if (this.videoType !== 'upload') return;

    // Check if this video already has an observer to prevent conflicts
    if (this.videoElement && this.videoElement.hasAttribute('data-video-observer-attached')) {
      console.warn('Video element already has an observer attached, skipping VideoSection observer');
      return;
    }

    const options = {
      root: null,
      rootMargin: '200px 0px',
      threshold: 0.5 // Use consistent threshold to match mobile optimization
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.handleVideoInView();
        } else {
          this.handleVideoOutOfView();
        }
      });
    }, options);

    this.observer.observe(this.section);
    
    // Mark video element as having an observer to prevent conflicts
    if (this.videoElement) {
      this.videoElement.setAttribute('data-video-observer-attached', 'true');
    }
  }

  bindEvents() {
    // Play button click with toggle functionality
    if (this.playButton) {
      this.playButton.addEventListener('click', () => this.togglePlayPause());
    }

    // YouTube thumbnail click
    if (this.youtubeThumb) {
      this.youtubeThumb.addEventListener('click', () => this.loadYouTubeEmbed());
    }

    // Vimeo thumbnail click
    if (this.vimeoThumb) {
      this.vimeoThumb.addEventListener('click', () => this.loadVimeoEmbed());
    }

    // Video events
    if (this.videoElement) {
      this.videoElement.addEventListener('canplay', () => {
        this.isVideoLoaded = true;
      });

      this.videoElement.addEventListener('play', () => {
        this.isPlaying = true;
        this.hidePoster();
        this.pauseOtherVideos();
        this.updatePlayButtonState();
      });

      this.videoElement.addEventListener('pause', () => {
        this.isPlaying = false;
        this.updatePlayButtonState();
        // When paused (e.g., via native controls), restore poster so the play button is centered
        if (this.playButton && this.posterElement) {
          this.showPoster();
        }
      });

      // Add click handler to video element based on interaction settings
      if (!this.settings.controls) {
        // Only make video clickable if there's a play button OR autoplay is enabled
        if (this.playButton || this.settings.autoplay) {
          this.videoElement.addEventListener('click', (e) => {
            e.preventDefault();
            this.togglePlayPause();
          });
          
          // Make video element appear clickable
          this.videoElement.style.cursor = 'pointer';
        } else {
          // No interaction allowed - remove pointer cursor
          this.videoElement.style.cursor = 'default';
        }
      }

      this.videoElement.addEventListener('ended', () => {
        this.isPlaying = false;
        this.updatePlayButtonState();
        if (!this.settings.loop) {
          this.showPoster();
        }
      });
    }

    // Resize listener for responsive aspect ratio updates
    if (this.videoType === 'lottie') {
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          const wrapper = this.section.querySelector('.video-section__wrapper');
          if (wrapper && (this.desktopAspectRatio || this.mobileAspectRatio)) {
            this.updateWrapperAspectRatio(wrapper);
          }
        }, 150); // Debounce resize events
      });
    }
  }

  initVideoType() {
    switch (this.videoType) {
      case 'upload':
        this.initUploadedVideo();
        break;
      case 'youtube':
        this.initYouTubeVideo();
        break;
      case 'vimeo':
        this.initVimeoVideo();
        break;
      case 'lottie':
        this.initLottieAnimation();
        break;
    }
  }

  initUploadedVideo() {
    if (!this.videoElement) return;

    // Load video source from data attribute
    const videoSrc = this.videoElement.dataset.videoSrc;
    if (videoSrc && !this.videoElement.querySelector('source')) {
      const source = document.createElement('source');
      source.src = videoSrc;
      if (window.innerWidth <= 767 && this.videoElement.dataset.videoSrcMobile != undefined) {
        source.src = this.videoElement.dataset.videoSrcMobile;
      }
      source.type = 'video/mp4';
      this.videoElement.appendChild(source);
      this.videoElement.load();
    }
  }

  initYouTubeVideo() {
    // YouTube thumbnail is already loaded
  }

  async initVimeoVideo() {
    if (!this.vimeoThumb) return;

    const vimeoId = this.vimeoThumb.dataset.vimeoId;
    if (!vimeoId) return;

    const thumbnailImg = this.vimeoThumb.querySelector('.vimeo-thumbnail');
    if (thumbnailImg && !thumbnailImg.src) {
    try {
      const response = await fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${vimeoId}`);
      const data = await response.json();
      
        if (data.thumbnail_url) {
        thumbnailImg.src = data.thumbnail_url;
      }
    } catch (error) {
        // Fail silently
      }
    }
  }

  initLottieAnimation() {
    // Check if Lottie library is available, with retry mechanism
    if (typeof lottie === 'undefined') {
      console.warn('VideoSection: Lottie library not loaded yet, retrying...');
      // Retry with exponential backoff
      this.lottieRetryCount = (this.lottieRetryCount || 0) + 1;
      if (this.lottieRetryCount < 20) { // Max 20 retries (about 2 seconds)
        setTimeout(() => {
          this.initLottieAnimation();
        }, 100 * this.lottieRetryCount);
      } else {
        console.error('VideoSection: Lottie library failed to load after multiple retries');
      }
      return;
    }

    // Initialize desktop Lottie animation
    if (this.lottieDesktopContainer) {
      this.initLottieForContainer(this.lottieDesktopContainer, 'desktop');
    }

    // Initialize mobile Lottie animation
    if (this.lottieMobileContainer) {
      this.initLottieForContainer(this.lottieMobileContainer, 'mobile');
    }

    // Set up intersection observer for Lottie animations
    this.setupLottieIntersectionObserver();
  }

  initLottieForContainer(container, type) {
    const lottieUrl = container.dataset.lottieUrl;
    const shouldLoop = container.dataset.lottieLoop === 'true';
    
    if (!lottieUrl) return;

    console.log(`VideoSection: Initializing ${type} Lottie animation with URL:`, lottieUrl);

    // Initialize Lottie animation
    const animation = lottie.loadAnimation({
      container: container,
      renderer: 'svg',
      loop: shouldLoop,
      autoplay: false, // We'll control this with intersection observer
      path: lottieUrl
    });

    // Store animation reference
    if (type === 'desktop') {
      this.lottieDesktopAnimation = animation;
    } else {
      this.lottieMobileAnimation = animation;
    }

    // Add event listeners for debugging and height calculation
    animation.addEventListener('DOMLoaded', () => {
      console.log(`VideoSection: ${type} Lottie animation DOM loaded`);
      
      // Calculate actual aspect ratio from Lottie animation dimensions
      const animationData = animation.animationData;
      if (animationData && animationData.w && animationData.h) {
        const aspectRatio = animationData.w / animationData.h;
        console.log(`VideoSection: ${type} Lottie actual dimensions:`, {
          width: animationData.w,
          height: animationData.h,
          aspectRatio: aspectRatio
        });
        
        // Update the wrapper's aspect ratio to match the actual video dimensions
        const wrapper = container.closest('.video-section__wrapper');
        if (wrapper) {
          // Store the aspect ratio for this animation type
          if (type === 'desktop') {
            this.desktopAspectRatio = aspectRatio;
          } else {
            this.mobileAspectRatio = aspectRatio;
          }
          
          // Apply the appropriate aspect ratio based on current viewport
          this.updateWrapperAspectRatio(wrapper);
        }
      }
    });

    animation.addEventListener('data_ready', () => {
      console.log(`VideoSection: ${type} Lottie animation data ready`);
    });

    animation.addEventListener('error', (error) => {
      console.error(`VideoSection: ${type} Lottie animation error:`, error);
    });
  }

  updateWrapperAspectRatio(wrapper) {
    const isMobile = window.innerWidth <= 768;
    let aspectRatio = null;
    
    if (isMobile && this.mobileAspectRatio) {
      aspectRatio = this.mobileAspectRatio;
      console.log('VideoSection: Using mobile aspect ratio:', aspectRatio);
    } else if (!isMobile && this.desktopAspectRatio) {
      aspectRatio = this.desktopAspectRatio;
      console.log('VideoSection: Using desktop aspect ratio:', aspectRatio);
    } else if (this.desktopAspectRatio) {
      // Fallback to desktop if mobile not available
      aspectRatio = this.desktopAspectRatio;
      console.log('VideoSection: Using desktop aspect ratio as fallback:', aspectRatio);
    }
    
    if (aspectRatio && wrapper) {
      wrapper.style.aspectRatio = aspectRatio;
      console.log('VideoSection: Updated wrapper aspect ratio to:', aspectRatio);
    }
  }

  setupLottieIntersectionObserver() {
    // Only set up observer if we have at least one Lottie animation
    if (!this.lottieDesktopAnimation && !this.lottieMobileAnimation) return;

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5
    };

    this.lottieObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.handleLottieInView();
        } else {
          this.handleLottieOutOfView();
        }
      });
    }, options);

    this.lottieObserver.observe(this.section);
  }

  handleLottieInView() {
    if (this.videoType === 'lottie' && this.settings.autoplayOnView) {
      // Play desktop animation if available
      if (this.lottieDesktopAnimation) {
        console.log('VideoSection: Desktop Lottie animation in view, playing');
        this.lottieDesktopAnimation.play();
      }
      
      // Play mobile animation if available
      if (this.lottieMobileAnimation) {
        console.log('VideoSection: Mobile Lottie animation in view, playing');
        this.lottieMobileAnimation.play();
      }
    } else {
      console.log('VideoSection: Lottie animation in view but not playing', {
        videoType: this.videoType,
        hasDesktopAnimation: !!this.lottieDesktopAnimation,
        hasMobileAnimation: !!this.lottieMobileAnimation,
        autoplayOnView: this.settings.autoplayOnView
      });
    }
  }

  handleLottieOutOfView() {
    if (this.videoType === 'lottie') {
      // Pause desktop animation if available
      if (this.lottieDesktopAnimation) {
        console.log('VideoSection: Desktop Lottie animation out of view, pausing');
        this.lottieDesktopAnimation.pause();
      }
      
      // Pause mobile animation if available
      if (this.lottieMobileAnimation) {
        console.log('VideoSection: Mobile Lottie animation out of view, pausing');
        this.lottieMobileAnimation.pause();
      }
    }
  }

  handleVideoInView() {
    // Clear any pending pause operations
    if (this.pauseTimeout) {
      clearTimeout(this.pauseTimeout);
      this.pauseTimeout = null;
    }

    // Only autoplay if autoplayOnView is enabled and video isn't already playing
    if (this.videoType === 'upload' && this.videoElement && !this.isPlaying && this.settings.autoplayOnView) {
      console.log('VideoSection: Video in view, attempting to play');
      
      // Debounce play operations to prevent rapid state changes
      if (this.playTimeout) {
        clearTimeout(this.playTimeout);
      }
      
      this.playTimeout = setTimeout(() => {
        this.playVideo();
      }, 100);
    }
  }

  handleVideoOutOfView() {
    // Clear any pending play operations
    if (this.playTimeout) {
      clearTimeout(this.playTimeout);
      this.playTimeout = null;
    }

    // AUTO PAUSE DISABLED - Commented out for now
    // Always pause when out of view to save bandwidth and improve UX
    // if (this.videoType === 'upload' && this.videoElement && this.isPlaying) {
    //   console.log('VideoSection: Video out of view, pausing');
    //   
    //   // Debounce pause operations to prevent rapid state changes
    //   if (this.pauseTimeout) {
    //     clearTimeout(this.pauseTimeout);
    //   }
    //   
    //   this.pauseTimeout = setTimeout(() => {
    //     this.pauseVideo();
    //     // Show poster when paused due to being out of view
    //     if (this.posterElement) {
    //       this.showPoster();
    //     }
    //   }, 100);
    // }
  }

  playVideo() {
    if (this.videoType === 'upload' && this.videoElement) {
      console.log('VideoSection: Attempting to play video', {
        sectionId: this.sectionId,
        isPlaying: this.isPlaying,
        videoReadyState: this.videoElement.readyState,
        videoPaused: this.videoElement.paused
      });
      
      this.videoElement.play().catch((error) => {
        console.warn('VideoSection: Autoplay failed:', error);
        // Don't update isPlaying state if play failed
      });
    }
  }

  pauseVideo() {
    if (this.videoType === 'upload' && this.videoElement) {
      console.log('VideoSection: Pausing video', {
        sectionId: this.sectionId,
        isPlaying: this.isPlaying,
        videoPaused: this.videoElement.paused
      });
      
      this.videoElement.pause();
    }
  }

  /**
   * Toggle play/pause when play button or video is clicked
   */
  togglePlayPause() {
    if (this.videoType === 'upload' && this.videoElement) {
      // Check if interaction is allowed
      const hasInteraction = this.playButton || (!this.settings.controls && this.settings.autoplay);
      
      if (hasInteraction) {
        if (this.isPlaying) {
          this.pauseVideo();
          // Show poster when manually paused (if there's a play button)
          if (this.playButton && this.posterElement) {
            this.showPoster();
          }
        } else {
          this.playVideo();
        }
      }
    } else {
      // For YouTube/Vimeo, just load the embed on first click
      if (this.videoType === 'youtube') {
        this.loadYouTubeEmbed();
      } else if (this.videoType === 'vimeo') {
        this.loadVimeoEmbed();
      }
    }
  }

  /**
   * Pause all other video instances (for same-slide behavior)
   */
  pauseOtherVideos() {
    videoInstances.forEach(instance => {
      if (instance !== this && instance.isPlaying && instance.videoType === 'upload') {
        instance.pauseVideo();
        // Update play button state and show poster for paused videos
        instance.updatePlayButtonState();
        instance.showPoster();
      }
    });
  }

  /**
   * Update play button icon based on video state
   */
  updatePlayButtonState() {
    if (!this.playButton || this.videoType !== 'upload') return;

    const playIcon = this.playButton.querySelector('svg');
    const useDefaultIcon = this.playButton?.dataset.default;
    if (!playIcon) return;

    if (this.isPlaying) {
      // Show pause icon
      if (useDefaultIcon == "true") {
        playIcon.innerHTML = '<g opacity="0.6"><circle cx="19.656" cy="19.8005" r="19.0525" fill="white"></circle><path d="M14 12h4v16H14V12z M22 12h4v16h-4V12" fill="black"></path></g>';
      }
      this.playButton.setAttribute('aria-label', 'Pause video');
    } else {
      // Show play icon
      if (useDefaultIcon == "true") {
        playIcon.innerHTML = '<g opacity="0.6"><circle cx="19.656" cy="19.8005" r="19.0525" fill="white"></circle><path d="M27.6457 17.2713C29.5928 18.3955 29.5928 21.2059 27.6456 22.3301L17.535 28.1675C15.5879 29.2916 13.154 27.8864 13.154 25.6381L13.154 13.9633C13.154 11.7149 15.5879 10.3097 17.535 11.4339L27.6457 17.2713Z" fill="black"></path></g>';
      }
      this.playButton.setAttribute('aria-label', 'Play video');
    }
  }

  hidePoster() {
    if (this.posterElement) {
      this.posterElement.style.opacity = '0';
      setTimeout(() => {
        this.posterElement.style.display = 'none';
      }, 300);
    }
  }

  showPoster() {
    if (this.posterElement) {
      // Restore natural display so Tailwind/class utilities (e.g., flex) can apply
      if (this.posterElement.classList.contains('flex')) {
        this.posterElement.style.display = 'flex';
      } else {
        this.posterElement.style.display = '';
      }
      setTimeout(() => {
        this.posterElement.style.opacity = '1';
      }, 50);
    }
  }

  loadYouTubeEmbed() {
    if (!this.youtubeThumb) return;

    const youtubeId = this.youtubeThumb.dataset.youtubeId;
    const playerContainer = this.section.querySelector('.video-section__youtube-player');
    
    if (!youtubeId || !playerContainer) return;

    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1`;
    iframe.width = '100%';
    iframe.height = '100%';
    iframe.frameBorder = '0';
    iframe.allow = 'autoplay; encrypted-media';
    iframe.allowFullscreen = true;
    iframe.style.cssText = 'position:absolute;top:0;left:0;';

    playerContainer.appendChild(iframe);
    playerContainer.classList.remove('hidden');
    this.youtubeThumb.style.display = 'none';
  }

  loadVimeoEmbed() {
    if (!this.vimeoThumb) return;

    const vimeoId = this.vimeoThumb.dataset.vimeoId;
    const playerContainer = this.section.querySelector('.video-section__vimeo-player');
    
    if (!vimeoId || !playerContainer) return;

    const iframe = document.createElement('iframe');
    iframe.src = `https://player.vimeo.com/video/${vimeoId}?autoplay=1&title=0&byline=0&portrait=0`;
    iframe.width = '100%';
    iframe.height = '100%';
    iframe.frameBorder = '0';
    iframe.allow = 'autoplay; fullscreen; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.style.cssText = 'position:absolute;top:0;left:0;';

    playerContainer.appendChild(iframe);
    playerContainer.classList.remove('hidden');
    this.vimeoThumb.style.display = 'none';
  }

  destroy() {
    // Clear any pending timeouts
    if (this.playTimeout) {
      clearTimeout(this.playTimeout);
      this.playTimeout = null;
    }
    if (this.pauseTimeout) {
      clearTimeout(this.pauseTimeout);
      this.pauseTimeout = null;
    }

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.lottieObserver) {
      this.lottieObserver.disconnect();
      this.lottieObserver = null;
    }

    if (this.lottieAnimation) {
      this.lottieAnimation.destroy();
      this.lottieAnimation = null;
    }

    if (this.lottieDesktopAnimation) {
      this.lottieDesktopAnimation.destroy();
      this.lottieDesktopAnimation = null;
    }

    if (this.lottieMobileAnimation) {
      this.lottieMobileAnimation.destroy();
      this.lottieMobileAnimation = null;
    }

    if (this.videoElement) {
      this.videoElement.pause();
      this.videoElement.removeAttribute('src');
      this.videoElement.load();
      // Remove observer marker
      this.videoElement.removeAttribute('data-video-observer-attached');
    }

    // Remove from initialized set and global registry
    initializedSections.delete(this.section);
    videoInstances.delete(this);
  }
}

// Handle Shopify theme editor
document.addEventListener('shopify:section:load', (event) => {
  if (event.detail.sectionId) {
    const section = document.querySelector(`[data-section-id="${event.detail.sectionId}"]`);
    if (section && (section.dataset.sectionType === 'video' || section.dataset.sectionType === 'video-with-content')) {
      new VideoSection(section);
    }
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  if (event.detail.sectionId) {
    const section = document.querySelector(`[data-section-id="${event.detail.sectionId}"]`);
    if (section && section.videoSectionInstance) {
      section.videoSectionInstance.destroy();
    }
  }
});

// Make VideoSection available globally for lazy loading
window.VideoSection = VideoSection;

export default VideoSection; 